// Header bell — the signed-in user's own notifications (PRD_InAppNotifications C8,
// C13, C16). v1 carries "OT rejected" and "Payroll rejected", written and cleared
// by the server triggers; the text arrives pre-rendered (C13), so this component
// knows nothing about notification types.
//
// A menu, not an info popover, so it is its own panel rather than PwPopover (C8):
// click toggles, outside click and Esc close, picking an item closes it. The count
// is unread, non-dismissed notifications.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Icon from "../icons/Icon";
import {
  subscribeToNotifications,
  markRead,
  markAllRead,
  dismiss,
} from "../../services/notificationService";
import "../../styles/components/notification-bell.css";

// C13 — the bell's accessible label.
export const bellLabel = (unread) => (unread === 0 ? "Notifications" : `Notifications, ${unread} unread`);

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const companyId = company?.id;
  const userDocId = user?.userData?.userDocId;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!companyId || !userDocId) return undefined;
    return subscribeToNotifications(companyId, userDocId, setItems);
  }, [companyId, userDocId]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const visible = items.filter((n) => !n.dismissedAt);
  const unread = visible.filter((n) => !n.readAt);

  const openItem = (n) => {
    setOpen(false);
    if (!n.readAt) markRead(companyId, n.id).catch((err) => console.error("Mark read failed:", err));
    navigate(`/company/${company.slug}/${n.link}`);
  };

  const onMarkAllRead = () => {
    markAllRead(companyId, unread.map((n) => n.id)).catch((err) => console.error("Mark all read failed:", err));
  };

  const onDismiss = (n) => {
    dismiss(companyId, n.id).catch((err) => console.error("Dismiss failed:", err));
  };

  return (
    <div className="nbell" ref={rootRef}>
      <button
        type="button"
        className="nbell-button"
        aria-label={bellLabel(unread.length)}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="bell" size={20} />
        {unread.length > 0 && (
          <span className="nbell-count" aria-hidden="true">{unread.length}</span>
        )}
      </button>

      {open && (
        <div className="nbell-panel" role="dialog" aria-label="Notifications">
          <div className="nbell-head">
            <span className="nbell-title">Notifications</span>
            {unread.length > 0 && (
              <button type="button" className="nbell-markall" onClick={onMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>

          {visible.length === 0 ? (
            <div className="nbell-empty">You're all caught up.</div>
          ) : (
            <ul className="nbell-list">
              {visible.map((n) => (
                <li key={n.id} className={`nbell-item${n.readAt ? "" : " nbell-item--unread"}`}>
                  <button type="button" className="nbell-item-main" onClick={() => openItem(n)}>
                    <span className="nbell-item-title">{n.title}</span>
                    <span className="nbell-item-body">{n.body}</span>
                  </button>
                  <button
                    type="button"
                    className="nbell-dismiss"
                    aria-label="Dismiss"
                    onClick={() => onDismiss(n)}
                  >
                    <Icon name="x" size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
